import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Store as StoreIcon, Link2, ShieldCheck, Loader2, Plug, PlugZap, AlertCircle } from "lucide-react";
import api, { API } from "@/lib/api";
import { useT } from "@/i18n";
import { formatApiError } from "@/context/AuthProvider";
import ConfirmActionDialog from "@/components/common/ConfirmActionDialog";

const STATUS_KEY = {
  connected: "shopify.statusConnected",
  disconnected: "shopify.statusDisconnected",
  uninstalled: "shopify.statusUninstalled",
};

export default function ShopifyConnect() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [toDisconnect, setToDisconnect] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shopify/status");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("connected")) {
      toast.success(t("shopify.connectedToast"));
      params.delete("connected"); params.delete("shop"); params.delete("host");
      setParams(params, { replace: true });
      load();
    } else if (params.get("error")) {
      toast.error(t("shopify.errorToast"));
      params.delete("error");
      setParams(params, { replace: true });
    }
  }, [params, setParams, t, load]);

  const connect = () => {
    const s = shop.trim();
    if (!s) { toast.error(t("shopify.shopPlaceholder")); return; }
    setConnecting(true);
    window.location.href = `${API}/shopify/install?shop=${encodeURIComponent(s)}`;
  };

  const disconnect = async () => {
    await api.post("/shopify/disconnect", { store_id: toDisconnect.store_id });
    toast.success(t("shopify.disconnectedToast"));
    setToDisconnect(null);
    load();
  };

  if (loading) {
    return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  }

  const connections = data?.connections || [];
  const connected = connections.filter((c) => c.connection?.status === "connected");

  return (
    <div className="max-w-3xl" data-testid="shopify-connect-page">
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
        <Plug className="h-6 w-6 text-[#5E8E3E]" />{t("shopify.title")}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("shopify.subtitle")}</p>

      {/* Read-only trust banner */}
      <div className="mt-5 flex items-start gap-3 rounded-lg border border-[#95BF47]/40 bg-[#95BF47]/10 p-4" data-testid="shopify-readonly-banner">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#5E8E3E]" />
        <p className="text-sm text-foreground">{t("shopify.readOnly")}</p>
      </div>

      {!data?.configured && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4" data-testid="shopify-not-configured">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <p className="text-sm text-foreground">{t("shopify.notConfigured")}</p>
        </div>
      )}

      {/* Connect card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-lg border border-border bg-card p-6" data-testid="shopify-connect-card">
        <label className="text-sm font-medium">{t("shopify.shopLabel")}</label>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
          <input
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && connect()}
            placeholder={t("shopify.shopPlaceholder")}
            disabled={!data?.configured || connecting}
            data-testid="shopify-domain-input"
            className="h-11 flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-brand disabled:opacity-50"
          />
          <button
            onClick={connect}
            disabled={!data?.configured || connecting}
            data-testid="shopify-connect-btn"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95 disabled:opacity-50"
          >
            {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {connecting ? t("shopify.connecting") : t("shopify.connectBtn")}
          </button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{t("shopify.importNote")}</p>
      </motion.div>

      {/* Connected stores */}
      <div className="mt-8">
        <h2 className="font-display text-lg font-bold">{t("shopify.connectedShops")}</h2>
        {connected.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="shopify-no-connections">{t("shopify.noConnections")}</p>
        ) : (
          <div className="mt-3 space-y-3" data-testid="shopify-connections-list">
            {connected.map((c) => {
              const conn = c.connection || {};
              const statusKey = STATUS_KEY[conn.status] || "shopify.statusNever";
              return (
                <div key={c.store_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4" data-testid={`shopify-conn-${c.store_id}`}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#95BF47]/15 text-[#5E8E3E]"><StoreIcon className="h-5 w-5" /></span>
                    <div className="min-w-0">
                      <p className="truncate font-medium">{c.shop_domain}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1 text-success"><PlugZap className="h-3 w-3" />{t(statusKey)}</span>
                        {conn.scopes?.length ? <span className="ml-2">· {t("shopify.scopes")}: {conn.scopes.join(", ")}</span> : null}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setToDisconnect(c)}
                    data-testid={`shopify-disconnect-${c.store_id}`}
                    className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    {t("shopify.disconnect")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmActionDialog
        open={!!toDisconnect}
        onOpenChange={(o) => !o && setToDisconnect(null)}
        title={t("shopify.disconnectTitle")}
        description={t("shopify.disconnectDesc")}
        resourceName={toDisconnect?.shop_domain}
        variant="destructive"
        confirmLabel={t("shopify.disconnect")}
        onConfirm={disconnect}
      />
    </div>
  );
}
